// @ts-check
import axios from 'axios';
import debugLogger from './debugLogger';
import { camelizeKeys } from 'humps';

let configuration = {
  defaultRejectionHandler: null,
};

export function configure(newConfig) {
  configuration = { ...configuration, ...newConfig };
}

function customCamelizeKeys(object) {
  return camelizeKeys(object, (key, convert) => {
    if (key.indexOf('-') >= 0) {
      // USe original key of it contains a dash (e.g. it's a UUID, etc.)
      return key;
    } else {
      return convert(key);
    }
  });
}

function serverTimeFromHeader(response) {
  const serverTimeString = response.headers['x-server-time'];
  if (serverTimeString) {
    return new Date(serverTimeString).getTime();
  }
  return undefined;
}

let csrfToken = null;

// Create instance of axios, to which we can add
// interceptors without affecting global axios.
const localAxiosInstance = axios.create();

localAxiosInstance.interceptors.request.use(config =>
  // prettier-ignore
  Object.assign({}, config, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
      'Accept': 'application/json',
    },
  })
);

// Custom behavior in response handler
localAxiosInstance.interceptors.response.use(response => {
  /* eslint-disable no-param-reassign */
  response.time = serverTimeFromHeader(response);
  /* eslint-enable no-param-reassign */

  if (response.data && response.data.csrf_token) {
    csrfToken = response.data.csrf_token;
    // console.log("set CSRF token to: ", csrfToken);
  }

  return response;
});

// always camelizeKeys in responses
localAxiosInstance.interceptors.response.use(
  response => {
    const newResponse = { ...response };
    if (response.data) newResponse.data = customCamelizeKeys(response.data);
    return newResponse;
  },
  error => {
    /* eslint-disable no-param-reassign */
    if (error.response) error.response.data = customCamelizeKeys(error.response.data);
    /* eslint-enable no-param-reassign */
    return Promise.reject(error);
  }
);

const defaultRejectionHandler = (...rest) => {
  if (configuration.defaultRejectionHandler) {
    return configuration.defaultRejectionHandler(...rest);
  }
  throw new Error('Trying to use default rejection handler, but none exists');
};

export { defaultRejectionHandler };

/**
 * Log response, then pass along
 */
function logResponse(response) {
  const {
    status,
    config: { method, url },
  } = response;
  const logString = `%c ${status}: [${method.toUpperCase()} ${url}] :`;
  debugLogger.log(logString, 'color: #4CAF50; font-weight: bold', response);
  return response;
}

/**
 * Log error, then rethrow
 */
function logError(error) {
  if (error.response) {
    debugLogger.log(
      '%c Request Error Response:',
      'color: #EC6060; font-weight: bold',
      error.response
    );
  } else {
    debugLogger.log('%c Request Error:', 'color: #EC6060; font-weight: bold', error);
  }
  throw error;
}

const injectCSRFRetry = (promise, verb, ...rest) => {
  // Automatically retry requests that failed due to CSRF, using the new token.
  // Currently this only occurs in the guest portal, because guest portal is the only place
  // that returns a token with a 403 CSRF failure response.
  return promise.catch(error => {
    if (
      error.response &&
      error.response.data &&
      error.response.data.errorCode === 'invalid_csrf' &&
      error.response.data.csrfToken
    ) {
      if (error.config && error.config.headers['X-CSRF-Token'] !== error.response.data.csrfToken) {
        csrfToken = error.response.data.csrfToken;
        return api[verb](...rest);
      }
    }
    throw error;
  });
};

const api = {
  defaultRejectionHandler,
};

const axiosVerbs = ['get', 'delete', 'head', 'post', 'put', 'patch'];

// Each axios verb returns a promise. We want
// to return a wrapper that delegates to axios,
// but adds some additional behavior such as
// logging and automatic rejection handling.
axiosVerbs.forEach(verb => {
  api[verb] = (...rest) => {
    let promise = localAxiosInstance[verb](...rest);

    // Use CSRF Retry
    promise = injectCSRFRetry(promise, verb, ...rest);

    // Dev logging
    if (process.env.NODE_ENV === 'development') promise = promise.then(logResponse, logError);

    // Always use default rejection handler. To avoid default handler, use `getWithoutDefaultRejectionHandling`, `postWithoutDefaultRejectionHandling`, etc.
    promise = promise.catch(defaultRejectionHandler);

    return promise;
  };
});

axiosVerbs.forEach(verb => {
  api[`${verb}WithoutDefaultRejectionHandling`] = (...rest) => {
    let promise = localAxiosInstance[verb](...rest);

    // Use CSRF Retry
    promise = injectCSRFRetry(promise, verb, ...rest);

    // Dev logging
    if (process.env.NODE_ENV === 'development') promise = promise.then(logResponse, logError);

    // Don't use default rejection handler, but embed it within the error object so that callers can use it. .
    promise = promise.catch(error => {
      error.defaultRejectionHandler = defaultRejectionHandler;
      throw error;
    });

    return promise;
  };
});

export default api;
